import { useIsFetching } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { toast } from '@/components/_shared/Toaster';
import i18n from '@/constants/i18n';
import { useUpdateCustomerProfileMutation } from '@/hooks/mutations/customer/useUpdateCustomerProfileMutation';
import { useCustomerQuery } from '@/hooks/queries/customer/useCustomerQuery';
import { useScreenTranslations } from '@/hooks/useScreenTranslations';

import type {
  LanguageOption,
  UseLanguageBottomSheetProps,
  UseLanguageBottomSheetReturn,
} from './types';

export const useLanguageBottomSheet = ({
  onDismiss,
}: UseLanguageBottomSheetProps): UseLanguageBottomSheetReturn => {
  const { t } = useScreenTranslations('LanguageBottomSheet');
  const [selectedLanguage, setSelectedLanguage] = useState<LanguageOption>(
    i18n.language as LanguageOption,
  );
  const [currentLanguage, setCurrentLanguage] = useState<LanguageOption>(
    i18n.language as LanguageOption,
  );

  const { data: customer } = useCustomerQuery();
  const { mutateAsync: updateCustomerProfile, isPending: isSyncing } =
    useUpdateCustomerProfileMutation();

  const isFetching = useIsFetching();
  const isLoading = isFetching > 0 || isSyncing;

  const [isWaitingForRefetch, setIsWaitingForRefetch] = useState(false);
  const [shouldClose, setShouldClose] = useState(false);
  const prevIsLoadingRef = useRef(isLoading);

  useEffect(() => {
    const wasLoading = prevIsLoadingRef.current;
    const finishedLoading = wasLoading && !isLoading;

    if (finishedLoading && isWaitingForRefetch) {
      setShouldClose(true);
      setIsWaitingForRefetch(false);
    }

    prevIsLoadingRef.current = isLoading;
  }, [isLoading, isWaitingForRefetch]);

  const isLanguageChanged = useMemo(
    () => selectedLanguage !== currentLanguage,
    [selectedLanguage, currentLanguage],
  );

  const getLanguageNameInCurrentLanguage = useMemo(() => {
    return i18n.getFixedT(currentLanguage);
  }, [currentLanguage]);

  const handleLanguageSelect = useCallback((language: LanguageOption) => {
    setSelectedLanguage(language);
  }, []);

  const handleConfirm = useCallback(async () => {
    if (!isLanguageChanged) return;

    if (customer?.id) {
      try {
        await updateCustomerProfile({
          customerId: customer.id,
          phone: customer.phone,
          firstName: customer.firstName,
          lastName: customer.lastName,
          email: customer.email,
          preferredLanguage: selectedLanguage,
        });
      } catch {
        toast({ variant: 'error', message: t('languageUpdateError') });
        return;
      }
    }

    i18n.changeLanguage(selectedLanguage);
    setCurrentLanguage(selectedLanguage);
    setIsWaitingForRefetch(true);
  }, [selectedLanguage, isLanguageChanged, customer, updateCustomerProfile, t]);

  const handleDismiss = useCallback(() => {
    setSelectedLanguage(currentLanguage);
    setIsWaitingForRefetch(false);
    setShouldClose(false);
    onDismiss?.();
  }, [onDismiss, currentLanguage]);

  const resetShouldClose = useCallback(() => {
    setShouldClose(false);
  }, []);

  const syncWithI18n = useCallback(() => {
    const currentLang = i18n.language as LanguageOption;
    setSelectedLanguage(currentLang);
    setCurrentLanguage(currentLang);
    setIsWaitingForRefetch(false);
    setShouldClose(false);
  }, []);

  return {
    selectedLanguage,
    currentLanguage,
    isLanguageChanged,
    isLoading,
    shouldClose,
    getLanguageNameInCurrentLanguage,
    handleLanguageSelect,
    handleConfirm,
    handleDismiss,
    syncWithI18n,
    resetShouldClose,
  };
};
